import React from 'react'

import { Checkbox, Form, Header, Image, Radio } from 'semantic-ui-react'

import {
  ATTRIBUTION_PREFIX_OPTIONS,
  AttributionPrefix,
  AttributionStyle,
  formatAttribution,
  ProjectAttributionSettings,
  WATERMARK_DURATION_OPTIONS,
  WATERMARK_POSITION_OPTIONS,
  WatermarkDuration,
  WatermarkPosition,
} from '../../utils/projectSetup'

import previewBackground from '../../_images/designer-clip-background.jpeg'

interface IProps {
  value: ProjectAttributionSettings
  onChange: (value: ProjectAttributionSettings) => void
  watermarkUrl?: string
  brandColor?: string
}

const ProjectOverlayFields = ({ value, onChange, watermarkUrl, brandColor = '#6C58FB' }: IProps) => {
  const update = (change: Partial<ProjectAttributionSettings>) => onChange({ ...value, ...change })
  const watermarkPositionStyle: React.CSSProperties = {
    position: 'absolute',
    width: '18%',
    maxHeight: '22%',
    objectFit: 'contain',
    opacity: 0.8,
    ...(value.watermarkPosition.startsWith('top') ? { top: '7%' } : { bottom: '7%' }),
    ...(value.watermarkPosition.endsWith('Right') ? { right: '4%' } : { left: '4%' }),
  }
  const sampleContributorName = '[Contributor name]'
  const attributionText = formatAttribution(value.attributionPrefix, sampleContributorName)

  return (
    <Form>
      <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: '18px', marginBottom: '18px' }}>
        <div style={{ flex: '0 0 300px', maxWidth: '100%', width: '300px' }}>
          <div
            data-testid="attribution-preview"
            style={{
              background: `#17213d url(${previewBackground}) center/cover no-repeat`,
              borderRadius: '8px',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.12)',
              overflow: 'hidden',
              paddingTop: '56.25%',
              position: 'relative',
              width: '100%',
            }}
          >
            {value.showWatermark &&
              (watermarkUrl ? (
                <Image src={watermarkUrl} alt="Your current video watermark" style={watermarkPositionStyle} />
              ) : (
                <div
                  style={{
                    ...watermarkPositionStyle,
                    border: '1px dashed rgba(255,255,255,.8)',
                    color: '#fff',
                    fontSize: '9px',
                    padding: '6px',
                    textAlign: 'center',
                  }}
                >
                  Watermark
                </div>
              ))}
            {value.showContributorAttribution && (
              <div
                style={{
                  position: 'absolute',
                  bottom: value.attributionStyle === 'lowerThird' ? '9%' : '8%',
                  left: '5%',
                  background: value.attributionStyle === 'lowerThird' ? `${brandColor}e0` : 'transparent',
                  boxSizing: 'border-box',
                  color: '#fff',
                  fontSize: value.attributionStyle === 'lowerThird' ? '11px' : '10px',
                  fontWeight: value.attributionStyle === 'lowerThird' ? 600 : 400,
                  maxWidth: '90%',
                  minWidth: value.attributionStyle === 'lowerThird' ? '55%' : undefined,
                  overflowWrap: 'anywhere',
                  padding: value.attributionStyle === 'lowerThird' ? '7px 10px' : 0,
                  textShadow: value.attributionStyle === 'plain' ? '0 1px 3px rgba(0,0,0,.85)' : 'none',
                }}
              >
                {attributionText}
              </div>
            )}
          </div>
        </div>
        <div style={{ color: '#666', flex: '1 1 280px', fontSize: '13px' }}>
          <strong>Preview</strong>
          <p style={{ margin: '4px 0' }}>This guide shows the layout that will be written into your video template.</p>
          <p style={{ margin: 0 }}>
            Watermark doesn’t look right? Change it in Branding before starting a project. Use a transparent white or
            light-grey PNG; a solid background will appear as a box.
          </p>
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gap: '22px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        }}
      >
        <section>
          <Header as="h3">Watermark your videos</Header>
          <p>Denote your brand ownership with a watermark at 80% opacity and comfortable edge spacing.</p>
          <Checkbox
            id="show-watermark"
            label="Show a watermark on each video"
            checked={value.showWatermark}
            onChange={(_, data) => update({ showWatermark: Boolean(data.checked) })}
          />
          {value.showWatermark && (
            <>
              <Form.Select
                label="Watermark position"
                options={WATERMARK_POSITION_OPTIONS}
                value={value.watermarkPosition}
                onChange={(_, data) => update({ watermarkPosition: data.value as WatermarkPosition })}
                style={{ marginTop: '12px' }}
              />
              <Form.Select
                label="Watermark duration"
                options={WATERMARK_DURATION_OPTIONS}
                value={value.watermarkDuration}
                onChange={(_, data) => update({ watermarkDuration: data.value as WatermarkDuration })}
              />
              <p style={{ color: '#666', fontSize: '12px' }}>
                A short watermark reduces processing work for long videos. You can keep it visible for the entire video
                when required.
              </p>
            </>
          )}
        </section>

        <section>
          <Header as="h3">Credit the contributor</Header>
          <p>Show who contributed each response, or leave the video without attribution.</p>
          <Checkbox
            id="show-contributor-attribution"
            label="Show contributor attribution on each response"
            checked={value.showContributorAttribution}
            onChange={(_, data) => update({ showContributorAttribution: Boolean(data.checked) })}
          />

          {value.showContributorAttribution && (
            <div style={{ marginTop: '12px' }}>
              <Form.Select
                label="Attribution wording"
                options={ATTRIBUTION_PREFIX_OPTIONS.map((prefix) => ({
                  key: prefix,
                  text: formatAttribution(prefix, sampleContributorName),
                  value: prefix,
                }))}
                value={value.attributionPrefix}
                onChange={(_, data) => update({ attributionPrefix: data.value as AttributionPrefix })}
              />
              <Form.Field>
                <label>Attribution style</label>
                <Radio
                  id="attribution-style-lower-third"
                  label="Boxed lower-third"
                  name="attributionStyle"
                  value="lowerThird"
                  checked={value.attributionStyle === 'lowerThird'}
                  onChange={(_, data) => update({ attributionStyle: data.value as AttributionStyle })}
                />
              </Form.Field>
              <Form.Field>
                <Radio
                  id="attribution-style-plain"
                  label="White text over the video"
                  name="attributionStyle"
                  value="plain"
                  checked={value.attributionStyle === 'plain'}
                  onChange={(_, data) => update({ attributionStyle: data.value as AttributionStyle })}
                />
              </Form.Field>
            </div>
          )}
        </section>
      </div>

      <p style={{ color: '#666', marginTop: '20px' }}>
        Other information collected from contributors—such as answers to your questions or location—can be added later
        in Template Builder.
      </p>
    </Form>
  )
}

export default ProjectOverlayFields
